import type { AsyncBelongsTo } from '@ember-data/model';
import Model, { attr, belongsTo } from '@ember-data/model';
import { isBefore, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import RecurringRule from 'garaje/models/recurring-rule';
import type VisitorProfile from 'garaje/models/visitor-profile';

export type VisitStatus = 'no_show' | 'pre_registered' | 'invited' | 'on_site' | 'signed_out';
export type VisitType = 'previous' | 'current' | 'upcoming' | 'no_show' | 'unknown';
export type VisitResource = {
  type: 'invite' | 'entry' | 'unknown';
  id: string;
};

export default class VisitCompositeModel extends Model {
  @belongsTo('visitor-profile', { async: true, inverse: 'visits' })
  declare visitorProfile: AsyncBelongsTo<VisitorProfile>;

  @attr('string') declare fullName: string;
  @attr('string') declare email: string;
  @attr('string') declare locationId: string;
  @attr('string') declare locationName: string;
  @attr('string') declare locationTimezone: string;
  @attr('string') declare flowName: string;
  @attr('string') declare host: string;
  @attr('string') declare status: VisitStatus;
  @attr('date') declare visitTime: Date;

  // previous visits
  @attr('date') declare signInTime: Date;
  @attr('date') declare signOutTime: Date;

  // upcoming visits
  @attr('date') declare expectedArrivalTime: Date;
  @attr('date') declare expectedDepartureTime: Date;
  @attr('date') declare connectCheckInTime: Date;
  @attr('string') declare recurringRule: string;

  get visitType(): VisitType {
    const visitTimeInLocationTz = toZonedTime(this.visitTime, this.locationTimezone);
    const todayInLocationTz = toZonedTime(new Date(), this.locationTimezone);
    const isBeforeToday = isBefore(visitTimeInLocationTz, startOfDay(todayInLocationTz));
    const { type } = this.visitResource;
    if (type === 'invite') {
      // If the invite was expected before today, it's a no show. Otherwise, it's upcoming.
      if (isBeforeToday) {
        return 'no_show';
      } else {
        return 'upcoming';
      }
    } else if (type === 'entry') {
      // If the entry happened before today, or the visitor has signed out, it's a previous visit. Otherwise, it's current.
      if (isBeforeToday || Boolean(this.signOutTime)) {
        return 'previous';
      } else {
        return 'current';
      }
    }
    return 'unknown';
  }

  get parsedRecurringRule(): RecurringRule {
    return RecurringRule.parse(this.recurringRule);
  }

  get visitResource(): VisitResource {
    // id is in the format of "invite:123" or "entry:456"
    let [resourceType, resourceId] = this.id.split(':');

    if (!resourceId) {
      resourceId = 'unknown';
    }

    switch (resourceType) {
      case 'invite':
      case 'entry':
        break;
      default:
        resourceType = 'unknown';
    }

    return {
      type: resourceType as VisitResource['type'],
      id: resourceId,
    };
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    visitComposite: VisitCompositeModel;
  }
}
